import { UpdateProfileInput } from '@clipr/lib';
import { action, computed, makeObservable, observable } from 'mobx';
import { WindowState } from '../../components/overlays/windowState';
import { Message, StoreNode, StoreNodeProps } from '../../store';
import { Store } from '../../store/store';
import { input, InputState } from '../input/inputState';

type OpenParams = {
  onSubmit: () => void;
  onCancel?: (() => void) | null;
  onClose?: (() => void) | null;
  onSuppress?: ((value: boolean) => void) | null;
  targetCount?: number | null;
  jobId?: string | null;
}

export class SpeakerIdConfirmationWindowState
  extends StoreNode {

  readonly nodeType = 'SpeakerIdConfirmationWindow';

  constructor(store: Store, props?: StoreNodeProps) {
    super(store, props);
    makeObservable(this, {
      onSubmit: observable,
      onCancel: observable,
      onClose: observable,
      onSuppress: observable,
      targetCount: observable,
      jobId: observable,
      showVisibilityCheck: computed,
      targetCountDisplay: computed,
      submit: action,
      open: action,
      close: action,
      cancel: action,
      onWindowExited: action,
      clearState: action,
      updateUserPermissions: action,
      onChangeVisibility: action,
    });
    this.window.listen(
      this.windowListener);
  }

  onSubmit: (() => void) | null = null;
  onCancel: (() => void) | null = null;
  onClose: (() => void) | null = null;
  onSuppress: ((value: boolean) => void) | null = null;
  targetCount: number = 0;
  jobId: string | null = null;

  private windowListener = (msg: Message<WindowState>) => {
    switch (msg.type) {
      case 'close':
      case 'outsideClick':
        this.close('close');
        break;
    }
  }
  readonly window = new WindowState(this.store);

  readonly showSpeakerIdConfirmation: InputState = input(this, {
    name: 'showSpeakerIdConfirmation',
    onChange: (input) => this.onChangeVisibility(!!input.value)
  });

  get showVisibilityCheck(): boolean {
    return !!this.jobId;
  }

  get targetCountDisplay(): string {
    return this.targetCount.toString() + ' ' + (this.targetCount === 1 ? 'speaker' : 'speakers');
  }

  async submit() {

    if (this.onSubmit)
      await this.onSubmit();

    this.close('close');
  }

  open(params: OpenParams) {
    this.dispatch('Overlays', 'openWindow', { name: 'SpeakerIdConfirmationWindow' });
    this.onSubmit = params.onSubmit;
    this.onCancel = params.onCancel ?? null;
    this.onClose = params.onClose ?? null;
    this.onSuppress = params.onSuppress ?? null;
    this.targetCount = params.targetCount ?? 0;
    this.jobId = params.jobId ?? null;
    this.emit('open');
    this.showSpeakerIdConfirmation.loadValue(false);
  }

  async close(msg?: string) {
    if (this.onClose)
      await this.onClose();

    this.dispatch('Overlays', 'closeWindow');
    if (msg) this.emit(msg);

    if (this.showSpeakerIdConfirmation.value) {
      this.updateUserPermissions();
    }
  }

  async cancel() {
    if (this.onCancel)
      await this.onCancel();

    this.close('close');
  }

  onWindowExited() {
    this.clearState();
  }

  clearState() {
    this.onSubmit = null;
    this.onCancel = null;
    this.onClose = null;
    this.onSuppress = null;
    this.targetCount = 0;
    this.jobId = null;
  }

  async updateUserPermissions() {

    if (!this.jobId)
      return;

    const params: { args: UpdateProfileInput } = {
      args: {
        hideSpeakerSuggestion: this.jobId,
      }
    };

    // TODO: replace manual updateProfile + runRefreshContextFlow with a unified speaker preference flow
    await this.store.api.updateProfile(params);
    await this.store.authService.runRefreshContextFlow();
  }

  async onChangeVisibility(value: boolean) {
    this.onSuppress?.(value);
  }

}